import { useState } from "react";
import { DataTable } from "../../components/DataTable";
import { StatusBadge } from "../../components/StatusBadge";
import { UserPlus, Ban, CheckCircle2, Trash2, SquarePen } from "lucide-react";
import { useUsers, useInviteUser, useUpdateUser, useDeleteUser, type User } from "../../api/users";
import { ApiError } from "../../api/client";

const ROLE_OPTIONS = [
  { value: "viewer", label: "Viewer" },
  { value: "operator", label: "Operator" },
  { value: "tenant_admin", label: "Admin" },
];

function errMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { message?: string } } | undefined;
    return body?.error?.message ?? fallback;
  }
  return fallback;
}

export function UserListPage() {
  const [showInvite, setShowInvite] = useState(false);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [role, setRole] = useState("viewer");
  const [error, setError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<null | { action: "disable" | "delete"; user: User }>(null);
  const [editing, setEditing] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({ firstName: "", lastName: "", email: "", role: "viewer" });
  const [editError, setEditError] = useState<string | null>(null);

  const { data } = useUsers();
  const invite = useInviteUser();
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await invite.mutateAsync({ email, role, firstName: firstName.trim(), lastName: lastName.trim() });
      setShowInvite(false);
      setEmail("");
      setFirstName("");
      setLastName("");
      setRole("viewer");
    } catch (err) {
      setError(errMessage(err, "Failed to invite user"));
    }
  }

  async function changeRole(user: User, nextRole: string) {
    if (nextRole === user.role) return;
    setRowError(null);
    try {
      await updateUser.mutateAsync({ userId: user.userId, role: nextRole });
    } catch (err) {
      setRowError(errMessage(err, "Failed to update role"));
    }
  }

  async function setActive(user: User) {
    setRowError(null);
    try {
      await updateUser.mutateAsync({ userId: user.userId, status: "active" });
    } catch (err) {
      setRowError(errMessage(err, "Failed to enable user"));
    }
  }

  function openEdit(user: User) {
    setEditError(null);
    setEditForm({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      role: user.role,
    });
    setEditing(user);
  }

  async function saveEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!editing) return;
    setEditError(null);
    try {
      await updateUser.mutateAsync({
        userId: editing.userId,
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        email: editForm.email,
        role: editForm.role,
      });
      setEditing(null);
    } catch (err) {
      setEditError(errMessage(err, "Failed to update user"));
    }
  }

  async function runConfirmedAction() {
    if (!confirm) return;
    const { action, user } = confirm;
    setRowError(null);
    try {
      if (action === "delete") {
        await deleteUser.mutateAsync({ userId: user.userId });
      } else {
        await updateUser.mutateAsync({ userId: user.userId, status: "deactivated" });
      }
    } catch (err) {
      setRowError(errMessage(err, action === "delete" ? "Failed to delete user" : "Failed to disable user"));
    } finally {
      setConfirm(null);
    }
  }

  const users = data?.data ?? [];
  const busy = updateUser.isPending || deleteUser.isPending;

  const columns = [
    { key: "email", header: "Email", sortable: true },
    { key: "firstName", header: "First Name", sortable: true },
    { key: "lastName", header: "Last Name", sortable: true },
    {
      key: "role",
      header: "Role",
      sortable: true,
      render: (row: User) =>
        row.role === "super_admin" ? (
          <span className="capitalize">super admin</span>
        ) : (
          <select
            aria-label={`Role for ${row.email}`}
            value={row.role}
            disabled={busy}
            onChange={(e) => void changeRole(row, e.target.value)}
            className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 disabled:opacity-50 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
          >
            {ROLE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        ),
    },
    { key: "status", header: "Status", render: (row: User) => <StatusBadge status={row.status} /> },
    { key: "createdAt", header: "Joined", render: (row: User) => new Date(row.createdAt).toLocaleDateString() },
    {
      key: "actions",
      header: "",
      render: (row: User) =>
        row.role === "super_admin" ? null : (
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => openEdit(row)}
              disabled={busy}
              className="flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
            >
              <SquarePen size={12} /> Edit
            </button>
            {row.status === "deactivated" ? (
              <button
                type="button"
                onClick={() => void setActive(row)}
                disabled={busy}
                className="flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 dark:border-riot-navy-600 dark:text-emerald-300 dark:hover:bg-riot-navy-700"
              >
                <CheckCircle2 size={12} /> Enable
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setConfirm({ action: "disable", user: row })}
                disabled={busy}
                className="flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs text-orange-700 hover:bg-orange-50 disabled:opacity-50 dark:border-riot-navy-600 dark:text-orange-300 dark:hover:bg-riot-navy-700"
              >
                <Ban size={12} /> Disable
              </button>
            )}
            <button
              type="button"
              onClick={() => setConfirm({ action: "delete", user: row })}
              disabled={busy}
              className="flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-riot-navy-600 dark:text-red-300 dark:hover:bg-riot-navy-700"
            >
              <Trash2 size={12} /> Delete
            </button>
          </div>
        ),
    },
  ];

  return (
    <div>
      {showInvite && (
        <form onSubmit={handleInvite} className="mb-4 rounded-lg border border-gray-200 bg-white dark:border-riot-navy-700 dark:bg-riot-navy-800 p-4">
          <h3 className="mb-3 text-sm font-medium text-gray-900 dark:text-white">Invite User</h3>
          <div className="flex flex-col gap-3 sm:flex-row">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="user@company.com"
              className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white"
            />
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="First name (optional)"
              aria-label="First name"
              className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white"
            />
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Last name (optional)"
              aria-label="Last name"
              className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white"
            />
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white"
              aria-label="Role"
            >
              <option value="viewer">Viewer</option>
              <option value="operator">Operator</option>
              <option value="tenant_admin">Admin</option>
            </select>
            <button
              type="submit"
              disabled={invite.isPending || !email}
              className="rounded-md bg-riot-gold-400 px-4 py-2 text-sm font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:opacity-50"
            >
              {invite.isPending ? "Sending..." : "Send Invite"}
            </button>
            <button
              type="button"
              onClick={() => { setShowInvite(false); setError(null); }}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-riot-navy-700"
            >
              Cancel
            </button>
          </div>
          {error && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>}
        </form>
      )}

      {rowError && <p className="mb-3 text-xs text-red-600 dark:text-red-400">{rowError}</p>}

      <DataTable
        title="Users"
        totalCount={data?.pagination.count ?? users.length}
        columns={columns}
        data={users}
        keyExtractor={(u) => u.userId}
        selectable
        filters={{
          searchPlaceholder: "Search users",
          searchFields: [{ value: "email", label: "Email" }],
          statusOptions: [
            { value: "active", label: "Active" },
            { value: "invited", label: "Invited" },
            { value: "deactivated", label: "Deactivated" },
          ],
        }}
        actions={
          <button
            onClick={() => setShowInvite(true)}
            className="flex items-center gap-1.5 rounded-md bg-riot-gold-400 px-3.5 py-1.5 text-xs font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600"
          >
            <UserPlus size={14} /> Invite User
          </button>
        }
      />

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div role="dialog" aria-modal="true" className="w-[min(440px,calc(100vw-2rem))] rounded-lg border border-gray-200 bg-white p-5 shadow-xl dark:border-riot-navy-700 dark:bg-riot-navy-800">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {confirm.action === "delete" ? "Delete user" : "Disable user"}
            </h3>
            <p className="mt-2 text-xs text-gray-600 dark:text-riot-navy-200">
              {confirm.action === "delete"
                ? `Deleting ${confirm.user.email} disables their sign-in and removes them from the list. Their record is retained for 30 days, then permanently removed — you can't undo this here.`
                : `Disabling ${confirm.user.email} blocks them from signing in until you re-enable them. Their data is kept.`}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirm(null)}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void runConfirmedAction()}
                className={
                  confirm.action === "delete"
                    ? "rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700"
                    : "rounded-md bg-orange-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-orange-700"
                }
              >
                {confirm.action === "delete" ? "Delete" : "Disable"}
              </button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <form
            onSubmit={(e) => void saveEdit(e)}
            role="dialog"
            aria-modal="true"
            className="w-[min(440px,calc(100vw-2rem))] rounded-lg border border-gray-200 bg-white p-5 shadow-xl dark:border-riot-navy-700 dark:bg-riot-navy-800"
          >
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Edit user</h3>
            <div className="mt-4 flex flex-col gap-3">
              <input
                type="text"
                aria-label="Edit first name"
                value={editForm.firstName}
                onChange={(e) => setEditForm((f) => ({ ...f, firstName: e.target.value }))}
                placeholder="First name"
                className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
              />
              <input
                type="text"
                aria-label="Edit last name"
                value={editForm.lastName}
                onChange={(e) => setEditForm((f) => ({ ...f, lastName: e.target.value }))}
                placeholder="Last name"
                className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
              />
              <input
                type="email"
                aria-label="Edit email"
                value={editForm.email}
                onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="Email"
                className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
              />
              <select
                aria-label="Edit role"
                value={editForm.role}
                onChange={(e) => setEditForm((f) => ({ ...f, role: e.target.value }))}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
              >
                {ROLE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            {editError && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{editError}</p>}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={updateUser.isPending}
                className="rounded-md bg-riot-gold-400 px-3 py-1.5 text-xs font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:opacity-50"
              >
                {updateUser.isPending ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
